// src/Animation/AnimationStack.ts
var AnimationStack = class {
  constructor() {
    /**
     * Holds all active (running) animations.
     */
    this.activeAnimations = [];
  }
  /**
   * Start the given animation.
   */
  start(animation) {
    animation.setOnFinish(() => {
      this.stop(animation);
    });
    this.activeAnimations.push(animation);
    animation.run();
  }
  /**
   * Stop the given animation.
   */
  stop(animation) {
    animation.destroy();
    const index = this.activeAnimations.indexOf(animation);
    if (index > -1) {
      this.activeAnimations.splice(index, 1);
    }
  }
  /**
   * Stop all running animations.
   */
  stopAll() {
    this.activeAnimations.forEach((animation) => {
      animation.destroy();
    });
    this.activeAnimations = [];
  }
};

// src/Animation/BaseAnimation.ts
var BaseAnimation = class {
  /**
   * Constructor
   */
  constructor(props) {
    /**
     * The abort delta that must be met to complete the animation.
     */
    this.abortDelta = 1;
    /**
     * The abort velocity that must be met to complete the animation.
     */
    this.abortVelocity = 50;
    /**
     * onUpdate callback
     * Called on each animation render loop with the new calculated value.
     */
    this.onUpdate = () => {
    };
    /**
     * onFinish callback
     * Called after onComplete.
     * Used internally to remove the animation from the animation stack.
     *
     * @internal
     */
    this.onFinish = () => {
    };
    /**
     * The internal render timer.
     *
     * @internal
     */
    this.renderTimer = null;
    this.start = props.start;
    this.end = props.end;
    if (props.abortDelta !== void 0) {
      this.abortDelta = props.abortDelta;
    }
    if (props.abortVelocity !== void 0) {
      this.abortVelocity = props.abortVelocity;
    }
    this.onUpdate = props.onUpdate;
    if (props.onComplete) {
      this.onComplete = props.onComplete;
    }
  }
  /**
   * Set the easing instance used in the animation.
   */
  setEaser(easer) {
    this.easer = easer;
  }
  /**
   * Run the animation.
   */
  run() {
    if (!this.easer) {
      console.error("Please set an easing instance via setEaser().", this);
      return;
    }
    this.prevTime = Date.now();
    this.deltaPosition = this.start - this.end;
    this.renderTimer = requestAnimationFrame(this.animationLoop.bind(this));
  }
  /**
   * Destroy the animation.
   * Called internally from the animation manager instance whenever the animation
   * should be stopped programmatically.
   *
   * @internal
   */
  destroy() {
    if (this.renderTimer !== null && this.renderTimer >= 0) {
      cancelAnimationFrame(this.renderTimer);
    }
    this.renderTimer = null;
  }
  /**
   * Set the onFinish callback.
   * Called internally from an animation manager instance with a onFinish callback
   * that remove the animation from the animation stack.
   *
   * @internal
   */
  setOnFinish(onFinish) {
    this.onFinish = onFinish;
  }
  /**
   * Run a single animation loop and
   * adjust the current animation value using the EasingInterface.
   *
   * Check abort conditions to complete the animation.
   * Otherwise, run onUpdate and start the next render loop.
   */
  animationLoop() {
    if (!this.renderTimer || !this.easer || !this.deltaPosition || !this.prevTime) {
      return;
    }
    if (this.renderTimer) {
      this.deltaPosition = this.easer.easeFrame(this.deltaPosition, Date.now() - this.prevTime);
      if (Math.abs(this.deltaPosition) < this.abortDelta && Math.abs(this.easer.velocity) < this.abortVelocity) {
        this.onUpdate(this.end);
        if (this.onComplete) {
          this.onComplete();
        }
        this.onFinish();
      } else {
        this.prevTime = Date.now();
        this.onUpdate(this.deltaPosition + this.end);
        this.renderTimer = requestAnimationFrame(this.animationLoop.bind(this));
      }
    }
  }
};

// src/Easer/SpringEaser.ts
var DEFAULT_DAMPING_RATIO = 0.75;
var DEFAULT_NATURAL_FREQUENCY = 12;
var SpringEaser = class {
  /**
   * Constructor
   *
   * @param initialVelocity Initial velocity in px per ms.
   * @param dampingRatio see SpringEaser.dampingRatio
   * @param naturalFrequency
   */
  constructor(initialVelocity, dampingRatio, naturalFrequency) {
    this.velocity = initialVelocity * 1e3;
    this.dampingRatio = dampingRatio || DEFAULT_DAMPING_RATIO;
    this.naturalFrequency = naturalFrequency || DEFAULT_NATURAL_FREQUENCY;
    if (this.dampingRatio > 1) {
      console.error("Please only use damping ratios <= 1.");
    }
    this.dampedFrequency = this.naturalFrequency * Math.sqrt(1 - this.dampingRatio * this.dampingRatio);
  }
  /**
   * Apply easing to the current animation frame.
   *
   * @param deltaPosition Difference between current and end position of the animation
   * @param deltaTime Frame duration in milliseconds
   */
  easeFrame(deltaPosition, deltaTime) {
    let displacement = 0;
    let coefficient;
    deltaTime /= 1e3;
    const naturalDumpingPow = Math.E ** (-this.dampingRatio * this.naturalFrequency * deltaTime);
    if (this.dampingRatio === 1) {
      coefficient = this.velocity + this.naturalFrequency * deltaPosition;
      displacement = (deltaPosition + coefficient * deltaTime) * naturalDumpingPow;
      this.velocity = displacement * -this.naturalFrequency + coefficient * naturalDumpingPow;
    } else if (this.dampingRatio < 1) {
      coefficient = 1 / this.dampedFrequency * (this.dampingRatio * this.naturalFrequency * deltaPosition + this.velocity);
      const dumpedFCos = Math.cos(this.dampedFrequency * deltaTime);
      const dumpedFSin = Math.sin(this.dampedFrequency * deltaTime);
      displacement = naturalDumpingPow * (deltaPosition * dumpedFCos + coefficient * dumpedFSin);
      this.velocity = displacement * -this.naturalFrequency * this.dampingRatio + naturalDumpingPow * (-this.dampedFrequency * deltaPosition * dumpedFSin + this.dampedFrequency * coefficient * dumpedFCos);
    }
    return displacement;
  }
};

// src/Animation/SpringAnimation.ts
var SpringAnimation = class extends BaseAnimation {
  constructor(props) {
    super(props);
    const { velocity, dampingRatio, naturalFrequency } = props;
    this.setEaser(new SpringEaser(velocity, dampingRatio, naturalFrequency));
  }
};

// src/Animation/util.ts
function clamp(val, min, max) {
  return Math.min(Math.max(val, min), max);
}
function decelerate(value, decelerationRate) {
  return value * decelerationRate / (1 - decelerationRate);
}
function equalizePointers(p1, p2) {
  p1.x = p2.x;
  p1.y = p2.y;
  if (typeof p2.id !== "undefined") {
    p1.id = p2.id;
  }
  return p1;
}
function pointsEqual(p1, p2) {
  return p1.x === p2.x && p1.y === p2.y;
}
function getDistanceBetween(p1, p2) {
  const x = Math.abs(p1.x - p2.x);
  const y = Math.abs(p1.y - p2.y);
  return Math.sqrt(x * x + y * y);
}
function getCenterOf(p1, p2) {
  return {
    x: (p1.x + p2.x) / 2,
    y: (p1.y + p2.y) / 2
  };
}

// src/Gestures/BaseGestures.ts
var AXIS_SWIPE_THRESHOLD = 10;
var DOUBLE_TAP_DELAY = 300;
var DOUBLE_TAP_MIN_DISTANCE = 25;
var BaseGestures = class {
  /**
   * Constructor
   *
   * @param animationsFactory A factory function to construct an animation manager instance.
   * @param dragHandlerFactory A factory function to construct a drag handler instance.
   * @param pinchHandlerFactory A factory function to construct a pinch handler instance.
   * @param tapHandlerFactory A factory function to construct a tap handler instance.
   */
  constructor(animationsFactory, dragHandlerFactory, tapHandlerFactory, pinchHandlerFactory) {
    /**
     * The calculated drag axis.
     */
    this.dragAxis = null;
    /**
     * The time between move events
     * used to calculate velocity.
     */
    this.intervalTime = null;
    /**
     * Tells if a drag is currently ongoing.
     */
    this.isDragging = false;
    /**
     * Tells if a multitouch zoom is currently ongoing.
     */
    this.isZooming = false;
    /**
     * Tells if the original gesture was a multitouch gesture.
     */
    this.isMultitouch = false;
    /**
     * The number of ongoing pointers.
     */
    this.numActivePointers = 0;
    /**
     * Holds all ongoing pointers for single point and multpoint (multitouch) events.
     */
    this.ongoingPointers = [];
    /**
     * Holds all determined options.
     */
    this.options = {
      touchEnabled: false,
      pointerEnabled: false,
      supportsTouch: false
    };
    /**
     * Tells if the pointer was pressed down.
     */
    this.pointerDown = false;
    /**
     * The render request animation frame timer.
     */
    this.renderTimer = null;
    /**
     * The tap timer used to determine if two separate tap events are close together to trigger a double tap.
     */
    this.tapTimer = null;
    /**
     * Tells if the velocity has been calculated.
     */
    this.velocityCalculated = false;
    /**
     * Click Event Handler
     */
    this.onClick = (event) => {
    };
    /**
     * Down Event Handler
     */
    this.onDown = (event) => {
      const isMousePointer = event.type === "mousedown" || event.pointerType === "mouse";
      if (isMousePointer && event.button > 0) {
        return;
      }
      if (this.preventDrag(event)) {
        event.preventDefault();
        return;
      }
      if (isMousePointer) {
        event.preventDefault();
      }
      this.bindEvent("move", this.onMove);
      this.bindEvent("up", this.onUp);
      this.bindEvent("cancel", this.onUp);
      this.updatePointers(event, "down");
      this.pointerDown = true;
      if (this.numActivePointers === 1) {
        this.dragAxis = null;
        equalizePointers(this.startP1, this.p1);
      }
      if (this.numActivePointers > 1) {
        this.clearTapTimer();
        this.isMultitouch = true;
      } else {
        this.isMultitouch = false;
      }
    };
    /**
     * Move Event Handler
     */
    this.onMove = (event) => {
      event.preventDefault();
      if (this.numActivePointers === 0) {
        return;
      }
      this.updatePointers(event, "move");
      if (this.numActivePointers === 1 && !this.isDragging) {
        if (!this.dragAxis) {
          this.calculateDragDirection();
        }
        if (this.dragAxis && !this.isDragging) {
          if (this.isZooming) {
            this.isZooming = false;
            this.pinch.end();
          }
          this.isDragging = true;
          this.clearTapTimer();
          this.updateStartPointers();
          this.intervalTime = Date.now();
          this.velocityCalculated = false;
          equalizePointers(this.intervalP1, this.p1);
          this.velocity.x = 0;
          this.velocity.y = 0;
          this.drag.start(event);
          this.clearRenderTimer();
          this.render();
        }
      } else if (this.numActivePointers > 1 && !this.isZooming) {
        this.finishDrag();
        this.isZooming = true;
        this.updateStartPointers();
        this.pinch.start();
        this.clearRenderTimer();
        this.render();
      }
    };
    /**
     * Up Event Handler
     */
    this.onUp = (event) => {
      if (!this.numActivePointers) {
        return;
      }
      this.updatePointers(event, "up");
      if (this.numActivePointers === 0) {
        this.pointerDown = false;
        this.clearRenderTimer();
        if (this.isDragging) {
          this.finishDrag();
        } else if (!this.isZooming && !this.isMultitouch) {
          this.finishTap(event);
        }
        this.unbindEvent("move");
        this.unbindEvent("up");
        this.unbindEvent("cancel");
      }
      if (this.numActivePointers < 2 && this.isZooming) {
        this.isZooming = false;
        if (this.pinch) {
          this.pinch.end();
        }
        if (this.numActivePointers === 1) {
          this.dragAxis = null;
          this.updateStartPointers();
        }
      }
    };
    this.render = () => {
      if (this.isDragging || this.isZooming) {
        this.updateVelocity();
        if (this.isDragging) {
          if (!pointsEqual(this.p1, this.previousP1)) {
            this.drag.change(this.p1, this.previousP1, this.dragAxis);
          }
        } else if (this.isZooming) {
          if (!pointsEqual(this.p1, this.previousP1) || !pointsEqual(this.p2, this.previousP2)) {
            if (this.pinch) {
              this.pinch.change(this.p1, this.startP1, this.p2, this.startP2);
            }
          }
        }
        this.updatePreviousPointers();
        this.renderTimer = requestAnimationFrame(this.render);
      }
    };
    this.options.touchEnabled = "ontouchstart" in window;
    this.options.pointerEnabled = !!window.PointerEvent;
    this.options.supportsTouch = this.options.touchEnabled || this.options.pointerEnabled && navigator.maxTouchPoints > 1;
    this.animations = animationsFactory();
    this.drag = dragHandlerFactory();
    if (pinchHandlerFactory) {
      this.pinch = pinchHandlerFactory();
    }
    if (tapHandlerFactory) {
      this.tap = tapHandlerFactory();
    }
  }
  /**
   * Disable all gestures.
   */
  disable() {
    this.unbindEvents();
    this.reset();
  }
  /**
   * Enable all gestures.
   */
  enable(el) {
    this.reset();
    this.el = el;
    this.eventMap = {
      pointer: {
        click: {
          eventName: "click",
          el: this.el
        },
        down: {
          eventName: "pointerdown",
          el: this.el
        },
        move: {
          eventName: "pointermove",
          el: window
        },
        up: {
          eventName: "pointerup",
          el: window
        },
        cancel: {
          eventName: "pointercancel",
          el: window
        }
      },
      touch: {
        click: {
          eventName: "click",
          el: this.el
        },
        down: {
          eventName: "touchstart",
          el: this.el
        },
        move: {
          eventName: "touchmove",
          el: window
        },
        up: {
          eventName: "touchend",
          el: window
        },
        cancel: {
          eventName: "touchcancel",
          el: window
        }
      },
      mouse: {
        click: {
          eventName: "click",
          el: this.el
        },
        down: {
          eventName: "mousedown",
          el: this.el
        },
        move: {
          eventName: "mousemove",
          el: window
        },
        up: {
          eventName: "mouseup",
          el: window
        },
        cancel: {
          eventName: null,
          el: null
        }
      }
    };
    this.bindEvent("click", this.onClick);
    this.bindEvent("down", this.onDown);
    if (this.options.touchEnabled) {
      el.ontouchmove = () => {
      };
      el.ontouchend = () => {
      };
    }
  }
  /**
   * Bind a single event with the given eventName + callback on the given element.
   */
  bindEvent(type, callback) {
    const deviceType = this.options.pointerEnabled ? "pointer" : this.options.touchEnabled ? "touch" : "mouse";
    const { el, eventName } = type === "click" ? { el: this.el, eventName: "click" } : this.eventMap[deviceType][type];
    if (el === null || eventName === null) {
      return;
    }
    el.addEventListener(eventName, callback);
    this.events[type] = {
      el,
      eventName,
      callback
    };
  }
  /**
   * Unbind an event for the given type.
   */
  unbindEvent(type) {
    if (this.events[type]) {
      const { el, eventName, callback } = this.events[type];
      el.removeEventListener(eventName, callback);
    }
  }
  /**
   * Calculate the drag direction.
   */
  calculateDragDirection() {
    const diff = Math.abs(this.p1.x - this.startP1.x) - Math.abs(this.p1.y - this.startP1.y);
    if (diff !== 0) {
      const shiftAxis = diff > 0 ? "x" : "y";
      if (Math.abs(this.p1[shiftAxis] - this.startP1[shiftAxis]) >= AXIS_SWIPE_THRESHOLD) {
        this.dragAxis = shiftAxis;
      }
    }
  }
  /**
   * Clear the render request animation frame timer.
   */
  clearRenderTimer() {
    if (this.renderTimer) {
      cancelAnimationFrame(this.renderTimer);
      this.renderTimer = null;
    }
  }
  /**
   * Clear the tab timer.
   */
  clearTapTimer() {
    if (this.tapTimer) {
      clearTimeout(this.tapTimer);
      this.tapTimer = null;
    }
  }
  /**
   * Convert the given event to a normalized pointer.
   *
   * @param event The received event.
   * @param p An existing gesture pointer to update.
   */
  applyEventPosToPointer(event, p) {
    p.x = event.clientX;
    p.y = event.clientY;
    if (typeof event.pointerId !== "undefined") {
      p.id = event.pointerId;
    } else if (event.identifier !== "undefined") {
      p.id = event.identifier;
    }
    return p;
  }
  /**
   * Finish an ongoing drag operation.
   */
  finishDrag() {
    if (!this.isDragging) {
      return;
    }
    this.isDragging = false;
    if (!this.velocityCalculated) {
      this.updateVelocity(true);
    }
    this.drag.end(this.velocity);
    this.dragAxis = null;
  }
  /**
   * Handle tapping after the pointer was released (pointer up).
   */
  finishTap(event) {
    if (event.type.indexOf("cancel") > 0) {
      return;
    }
    if (event.type === "mouseup" || event.pointerType === "mouse") {
      if (this.tap) {
        this.tap.click(event);
      }
      return;
    }
    if (this.tapTimer) {
      this.clearTapTimer();
      if (this.tapStartP1 === null) {
        return;
      }
      if (getDistanceBetween(this.tapStartP1, this.startP1) < DOUBLE_TAP_MIN_DISTANCE) {
        if (this.tap) {
          this.tap.doubleTap(event);
        }
        return;
      }
    }
    equalizePointers(this.tapStartP1, this.startP1);
    this.tapTimer = window.setTimeout(() => {
      if (this.tap) {
        this.tap.tap(event);
      }
      this.clearTapTimer();
    }, DOUBLE_TAP_DELAY);
  }
  /**
   * Get velocity for axis.
   */
  getVelocity(axis, duration) {
    const displacement = this.p1[axis] - this.intervalP1[axis];
    if (Math.abs(displacement) > 1 && duration > 5) {
      return displacement / duration;
    }
    return 0;
  }
  /**
   * Reset state.
   */
  reset() {
    this.clearTapTimer();
    this.clearRenderTimer();
    this.events = {
      cancel: null,
      click: null,
      down: null,
      move: null,
      up: null
    };
    this.p1 = {};
    this.p2 = {};
    this.dragAxis = null;
    this.intervalTime = null;
    this.intervalP1 = {};
    this.intervalP2 = {};
    this.isDragging = false;
    this.isMultitouch = false;
    this.isZooming = false;
    this.ongoingPointers = [];
    this.numActivePointers = 0;
    this.pointerDown = false;
    this.previousP1 = {};
    this.previousP2 = {};
    this.startP1 = {};
    this.startP2 = {};
    this.tapStartP1 = {};
    this.velocity = {
      x: 0,
      y: 0
    };
    this.velocityCalculated = false;
  }
  /**
   * Unbind all bound events.
   */
  unbindEvents() {
    Object.keys(this.events).forEach((type) => {
      this.unbindEvent(type);
    });
  }
  /**
   * Parse and normalize the pointers from touch, mouse or pointer events.
   *
   * @param event
   * @param pointerType The normalized pointer type ('up', 'down', 'move')
   */
  updatePointers(event, pointerType) {
    if (this.options.pointerEnabled) {
      const pointerIndex = this.ongoingPointers.findIndex((ongoingPointer) => {
        return ongoingPointer.id === event.pointerId;
      });
      if (pointerType === "up" && pointerIndex > -1) {
        this.ongoingPointers.splice(pointerIndex, 1);
      } else if (pointerType === "down" && pointerIndex === -1) {
        this.ongoingPointers.push(this.applyEventPosToPointer(event, { id: null, x: 0, y: 0 }));
      } else if (pointerIndex > -1) {
        this.applyEventPosToPointer(event, this.ongoingPointers[pointerIndex]);
      }
      this.numActivePointers = this.ongoingPointers.length;
      if (this.numActivePointers > 0) {
        equalizePointers(this.p1, this.ongoingPointers[0]);
      }
      if (this.numActivePointers > 1) {
        equalizePointers(this.p2, this.ongoingPointers[1]);
      }
    } else {
      this.numActivePointers = 0;
      if (event.type.indexOf("touch") > -1) {
        if (event.touches && event.touches.length > 0) {
          this.applyEventPosToPointer(event.touches[0], this.p1);
          this.numActivePointers++;
          if (event.touches.length > 1) {
            this.applyEventPosToPointer(event.touches[1], this.p2);
            this.numActivePointers++;
          }
        }
      } else {
        this.applyEventPosToPointer(event, this.p1);
        if (pointerType === "up") {
          this.numActivePointers = 0;
        } else {
          this.numActivePointers++;
        }
      }
    }
  }
  /**
   * Update the previous pointers.
   */
  updatePreviousPointers() {
    equalizePointers(this.previousP1, this.p1);
    equalizePointers(this.previousP2, this.p2);
  }
  /**
   * Update the start pointers.
   */
  updateStartPointers() {
    equalizePointers(this.startP1, this.p1);
    equalizePointers(this.startP2, this.p2);
    this.updatePreviousPointers();
  }
  /**
   * Update velocity at 50ms interval.
   */
  updateVelocity(force = false) {
    const time = Date.now();
    const duration = time - this.intervalTime;
    if (duration < 50 && !force) {
      return;
    }
    this.velocity.x = this.getVelocity("x", duration);
    this.velocity.y = this.getVelocity("y", duration);
    this.intervalTime = time;
    equalizePointers(this.intervalP1, this.p1);
    this.velocityCalculated = true;
  }
  /**
   * Prevent dragging when certain conditions are met.
   */
  preventDrag(event) {
    return false;
  }
};

// src/Gestures/BaseDragHandler.ts
var BaseDragHandler = class {
  /**
   * Constructor
   */
  constructor(animations) {
    /**
     * The starting pan point.
     */
    this.startPan = {};
    this.animations = animations;
  }
};

// src/Gestures/BasePinchHandler.ts
var BasePinchHandler = class {
  /**
   * Constructor
   */
  constructor(animations) {
    this.animations = animations;
  }
};

// src/Gestures/BaseTapHandler.ts
var BaseTapHandler = class {
  /**
   * Constructor
   */
  constructor(animations) {
    this.animations = animations;
  }
};

// src/util/animate.ts
function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
}
function easeOutCubic(t) {
  return --t * t * t + 1;
}
function linear(t) {
  return t;
}
function animationPosition(start, end, elapsed, duration, timingFunction) {
  if (elapsed > duration) {
    return end;
  }
  return start + (end - start) * timingFunction(elapsed / duration);
}

// src/util/getTransitionDuration.ts
import { getVariableContent } from "@sandstein/bitv-common";
function getTransitionDuration(cssVariableName, baseEl = document.documentElement) {
  const durationString = getVariableContent(cssVariableName, baseEl);
  const isMilliseconds = durationString.includes("ms");
  if (isMilliseconds) {
    return parseInt(durationString.split("ms").join("").trim());
  }
  return Math.floor(parseFloat(durationString.split("s").join("").trim()) * 1e3);
}

// src/util/getTransitionEndEvent.ts
var getTransitionEndEvent = () => {
  const transitions = {
    transition: "transitionend",
    OTransition: "oTransitionEnd",
    MozTransition: "transitionend",
    WebkitTransition: "webkitTransitionEnd"
  };
  const bodyStyle = document.body.style;
  for (let transition in transitions) {
    if (bodyStyle[transition] !== void 0) {
      return transitions[transition];
    }
  }
  return null;
};

// src/util/prefersReducedMotion.ts
function prefersReducedMotion() {
  return window.matchMedia("screen and (prefers-reduced-motion: reduce)").matches;
}

// src/util/scrollTo.ts
function getAbsoluteOffset(el, context = window) {
  const rect = el.getBoundingClientRect();
  if (context === window) {
    return {
      left: rect.left + context.scrollX,
      top: rect.top + context.scrollY
    };
  }
  return {
    left: rect.left + context.scrollLeft,
    top: rect.top + context.scrollTop
  };
}
function smoothScrollTo(targetY, duration = 500, callback, context = window) {
  if (prefersReducedMotion()) {
    if (context === window) {
      window.scroll(0, targetY);
    } else {
      context.scrollTop = targetY;
    }
    if (typeof callback === "function") {
      callback();
    }
    return;
  }
  const startY = context.scrollTop || window.scrollY || window.pageYOffset;
  const clock = Date.now();
  function step() {
    const elapsed = Date.now() - clock;
    if (context !== window) {
      context.scrollTop = animationPosition(startY, targetY, elapsed, duration, easeInOutCubic);
    } else {
      window.scroll(0, animationPosition(startY, targetY, elapsed, duration, easeInOutCubic));
    }
    if (elapsed > duration) {
      if (typeof callback === "function") {
        callback();
      }
    } else {
      window.requestAnimationFrame(step);
    }
  }
  step();
}
function scrollToElement(el, offset = 0, duration = 500, callback, context, skipFocus) {
  if (el === null) {
    return;
  }
  const computedStyle = getComputedStyle(el);
  if (computedStyle.display === "none" || computedStyle.visibility === "hidden") {
    return;
  }
  const targetY = getAbsoluteOffset(el).top + offset;
  smoothScrollTo(targetY, duration, () => {
    if (skipFocus !== true) {
      const isFocusable = el.getAttribute("tabindex") === "0" || ["a", "button", "input", "textarea", "select", "details"].includes(el.tagName.toLocaleLowerCase()) && el.getAttribute("tabindex") !== "-1";
      if (!isFocusable) {
        el.setAttribute("tabindex", "-1");
      }
      el.focus({
        preventScroll: true
      });
      if (!isFocusable) {
        el.removeAttribute("tabindex");
      }
    }
    if (callback) {
      callback();
    }
  }, context);
}
function scrollElementIntoView(el, offset = 0, duration = 500, callback, context = window) {
  if (el === null) {
    return;
  }
  const computedStyle = getComputedStyle(el);
  if (computedStyle.display === "none" || computedStyle.visibility === "hidden") {
    return;
  }
  let targetY = false;
  if (context === window) {
    const { top, bottom } = el.getBoundingClientRect();
    if (top < 0) {
      targetY = window.scrollY + top - offset;
    } else if (bottom > window.innerHeight) {
      targetY = window.scrollY - (window.innerHeight - bottom) + offset;
    }
  } else {
    const elTop = el.offsetTop;
    const elBottom = elTop + el.clientHeight;
    const containerTop = context.scrollTop;
    const containerBottom = containerTop + context.clientHeight;
    if (elTop < containerTop) {
      targetY = containerTop - (containerTop - elTop) - offset;
    } else if (elBottom > containerBottom) {
      targetY = containerTop + elBottom - containerBottom + offset;
    }
  }
  if (targetY === false) {
    if (typeof callback === "function") {
      callback();
    }
    return;
  }
  smoothScrollTo(targetY, duration, callback, context);
}
function scrollToAnchor(anchor, offset = 0, duration = 500, callback, context) {
  if (anchor === "#" || !anchor.startsWith("#")) {
    return;
  }
  const anchorId = anchor.split("#").join("");
  const targetElement = document.getElementById(anchorId);
  if (!targetElement)
    return;
  scrollToElement(targetElement, offset, duration, () => {
    if (callback) {
      callback();
    }
  }, context);
}

// src/util/scrollIntoViewIfNeeded.ts
var elementIsFullyVisibleInWindow = function(el, offsetTop = 0, offsetBottom = 0) {
  const { top, bottom } = el.getBoundingClientRect();
  const windowTop = offsetTop;
  const windowBottom = window.innerHeight - offsetBottom;
  return top >= windowTop && bottom <= windowBottom;
};
var elementIsPartiallyVisibleInWindow = function(el, offsetTop = 0, offsetBottom = 0) {
  const { top, bottom } = el.getBoundingClientRect();
  const windowTop = offsetTop;
  const windowBottom = window.innerHeight - offsetBottom;
  return top < windowBottom && bottom >= windowTop;
};
var scrollIntoViewIfNeeded = function(el, scrollOptions = { behavior: "smooth", block: "start", inline: "nearest" }) {
  if (!elementIsFullyVisibleInWindow(el)) {
    el.scrollIntoView(scrollOptions);
  }
};
var scrollIntoViewWithOffsetIfNeeded = function(el, offsetTop = 0, offsetBottom = 0) {
  if (!elementIsFullyVisibleInWindow(el, offsetTop, offsetBottom)) {
    scrollToElement(el, -offsetTop);
  }
};
export {
  AnimationStack,
  BaseAnimation,
  BaseDragHandler,
  BaseGestures,
  BasePinchHandler,
  BaseTapHandler,
  SpringAnimation,
  SpringEaser,
  animationPosition,
  clamp,
  decelerate,
  easeInOutCubic,
  easeOutCubic,
  elementIsFullyVisibleInWindow,
  elementIsPartiallyVisibleInWindow,
  equalizePointers,
  getCenterOf,
  getDistanceBetween,
  getTransitionDuration,
  getTransitionEndEvent,
  linear,
  pointsEqual,
  prefersReducedMotion,
  scrollElementIntoView,
  scrollIntoViewIfNeeded,
  scrollIntoViewWithOffsetIfNeeded,
  scrollToAnchor,
  scrollToElement
};
